import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Production builds swallow render errors silently — at least log them
    // to the console so the user can copy/paste if they report the screen.
    console.error('Jotly render error:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div className="rc-kicker" style={{ marginBottom: 8 }}>Jotly</div>
          <h1 style={titleStyle}>Something broke.</h1>
          <p style={hintStyle}>
            The app hit an error before it could render. Reloading usually fixes it.
          </p>
          <pre style={detailStyle}>{this.state.error.message}</pre>
          <button onClick={() => window.location.reload()} style={buttonStyle}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: 'var(--rc-card)',
  borderRadius: 18,
  padding: '34px 32px 28px',
  boxShadow: '0 12px 36px rgba(20,22,27,0.06), 0 1px 0 rgba(20,22,27,0.03)',
  border: '1px solid var(--rc-border)',
};

const titleStyle: React.CSSProperties = {
  font: '700 28px/1.1 var(--rc-display)',
  margin: '0 0 12px',
  letterSpacing: '-0.02em',
};

const hintStyle: React.CSSProperties = {
  margin: '0 0 16px',
  font: '400 14px/1.5 var(--rc-sans)',
  color: 'var(--rc-ink-2)',
};

const detailStyle: React.CSSProperties = {
  margin: '0 0 20px',
  padding: '10px 12px',
  borderRadius: 10,
  background: 'var(--rc-danger-soft)',
  color: 'var(--rc-danger)',
  font: '500 12px/1.5 var(--rc-mono)',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const buttonStyle: React.CSSProperties = {
  appearance: 'none',
  border: 0,
  cursor: 'pointer',
  padding: '12px 16px',
  borderRadius: 12,
  background: 'var(--rc-ink)',
  color: '#fff',
  font: '600 14px/1 var(--rc-sans)',
};
